import { useEffect, useState } from 'react';
import { useNotify, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { SectionCard } from '../shared/Panel';
import { useHasPermission } from '../admin/PermissionGate';
import { toErrorMessage } from './preOrderApi';
import {
  fetchPreOrderWriteDetails,
  isMissing,
  readSendReadiness,
  savePreOrderWriteDetails,
  type ReadinessItem,
  type SendReadiness,
} from './preOrderDocumentsApi';
import { ORDERING_PARTY_FIELDS, STATION_FIELDS } from '../order/WriteDetails';
import { typeNeedsForklift, type ForkliftDetails, type OrderingParty, type WriteDetails, type WriteStation } from '../order/viaWriteDetailsApi';

/**
 * Stan „gotowe do wysłania” i pola pliku dla importera na prezleceniu
 * (03.11.2026, `preOrderDocuments`, S-03.11-01; reguły w API:
 * `pre-order/send-readiness.ts`, `pre-order-write-details.ts`).
 *
 * Prezlecenie wolno zapisać niekompletne — każde pole jest opcjonalne. Komplet
 * jest potrzebny do utworzenia zlecenia (akceptacja) i do zapisu w TMS (pola
 * obowiązkowe importera); stan liczy API tym samym walidatorem co plik, a ekran
 * tylko go pokazuje i zaznacza brakujące pola. O wysłaniu decyduje człowiek.
 */

const GROUP_LABELS: Record<ReadinessItem['group'], string> = {
  acceptance: 'Do utworzenia zlecenia',
  preOrder: 'Dane prezlecenia do pliku',
  orderingParty: 'Zleceniodawca',
  shipment: 'Ładunek',
  station: 'Punkty',
  forklift: 'Czynność wózkowa',
  file: 'Plik',
};

/** Karta: gotowe / lista braków po grupach. */
export const SendReadinessCard = ({ readiness }: { readiness: SendReadiness | null }) => {
  if (!readiness) return null;
  // Grupy w kolejności pierwszego braku (bez iteracji po `Map` — cel kompilacji ES5).
  const groups: Array<[string, ReadinessItem[]]> = [];
  for (const m of readiness.missing) {
    const g = groups.find(([k]) => k === m.group);
    if (g) g[1].push(m);
    else groups.push([m.group, [m]]);
  }
  return (
    <SectionCard title="Gotowe do wysłania">
      <Box data-testid="pre-order-send-readiness" data-ready={readiness.ready ? '1' : '0'}>
        {readiness.ready ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            Komplet pól do zlecenia i do zapisu w TMS. Zlecenie tworzy i zapis zatwierdza człowiek — system niczego nie
            wysyła sam.
          </Alert>
        ) : (
          <>
            <Alert severity="warning" icon={<ErrorOutlineIcon />} sx={{ mb: 1 }}>
              {readiness.acceptanceReady
                ? 'Zlecenie można już utworzyć, ale do zapisu w TMS brakuje pól.'
                : 'Prezlecenie jest zapisane, ale do zlecenia i zapisu w TMS brakuje pól.'}{' '}
              Brakujące pola są zaznaczone w formularzu.
            </Alert>
            <Stack spacing={1} data-testid="pre-order-missing-list">
              {groups.map(([group, items]) => (
                <Box key={group}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {GROUP_LABELS[group as ReadinessItem['group']] ?? group}
                  </Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.25 }}>
                    {items.map((m) => (
                      <Chip key={m.key} size="small" variant="outlined" color="warning" label={m.label} data-missing-key={m.key} />
                    ))}
                  </Stack>
                </Box>
              ))}
              {readiness.fileErrors.length > 0 ? (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Plik nie przeszedłby importu
                  </Typography>
                  {readiness.fileErrors.map((e) => (
                    <Typography key={e} variant="body2" data-testid="pre-order-file-error">
                      {e}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Stack>
          </>
        )}
      </Box>
    </SectionCard>
  );
};

const FIELD_GRID = 'repeat(auto-fill, minmax(min(100%, 180px), 1fr))';
const asText = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/** Godzina i dzień w czasie polskim z chwili w bazie (UTC). */
function polishParts(iso: unknown): { date: string | null; time: string | null } {
  if (typeof iso !== 'string' || !iso) return { date: null, time: null };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: null, time: null };
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
}

/**
 * Punkty z nagłówka prezlecenia — tylko to, co w nim jest (miasto, kraj, kod,
 * początek okna). Reszty (firma, ulica, koniec okna, jednostki) nie
 * zgadujemy: zostaje pusta i jest na liście braków.
 */
export function stationsFromHeader(record: RaRecord): WriteStation[] {
  const point = (prefix: 'loading' | 'unloading'): WriteStation => {
    const when = polishParts(record[`${prefix}Date`]);
    return {
      city: (record[`${prefix}City`] as string | null) ?? null,
      countryId: (record[`${prefix}Country`] as string | null) ?? null,
      zip: (record[`${prefix}PostalCode`] as string | null) ?? null,
      fromDate: when.date,
      fromTime: when.time,
    };
  };
  return [point('loading'), point('unloading')];
}

/** Pola pliku dla importera — formularz na prezleceniu. */
export const PreOrderImporterFields = ({ record, onSaved }: { record: RaRecord; onSaved: () => void }) => {
  const notify = useNotify();
  const canEdit = useHasPermission('ORDER_INTAKE');
  const [details, setDetails] = useState<WriteDetails | null>(null);
  const [readiness, setReadiness] = useState<SendReadiness | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const preOrderId = record.id as number;

  useEffect(() => {
    let alive = true;
    fetchPreOrderWriteDetails(preOrderId)
      .then((w) => {
        if (!alive) return;
        setDetails(w.details);
        setReadiness(w.readiness);
        setRefusal(w.refusal);
      })
      .catch((e) => alive && setError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
  }, [preOrderId]);

  const readOnly = !canEdit || refusal !== null;
  const missing = (key: string) => isMissing(readiness ?? readSendReadiness(record.sendReadiness), key);
  const stations = details?.stacje ?? [];
  const party = details?.zleceniodawca ?? {};
  const forkliftRequired = typeNeedsForklift(record.requiredVehicleType as string | null);

  const setParty = (k: keyof OrderingParty, v: string) =>
    setDetails((d) => ({ ...(d ?? {}), zleceniodawca: { ...(d?.zleceniodawca ?? {}), [k]: v } }));
  const setStation = (i: number, k: keyof WriteStation, v: string) =>
    setDetails((d) => {
      const list = [...(d?.stacje ?? [])];
      list[i] = { ...(list[i] ?? {}), [k]: k === 'transportUnitCount' ? (v === '' ? null : Number(v)) : v };
      return { ...(d ?? {}), stacje: list };
    });
  const setForklift = (k: keyof ForkliftDetails, v: string) =>
    setDetails((d) => ({ ...(d ?? {}), forklift: { ...(d?.forklift ?? {}), [k]: v === '' ? null : Number(v) } }));

  const save = async () => {
    if (!details) return;
    setBusy(true);
    try {
      const w = await savePreOrderWriteDetails(preOrderId, details);
      setDetails(w.details);
      setReadiness(w.readiness);
      setRefusal(w.refusal);
      setError(null);
      notify('Zapisano pola dla importera.', { type: 'info' });
      onSaved();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const field = (key: string, label: string, value: unknown, onChange: (v: string) => void, type?: string) => (
    <TextField
      key={key}
      size="small"
      label={label}
      type={type ?? 'text'}
      value={asText(value)}
      onChange={(e) => onChange(e.target.value)}
      disabled={readOnly}
      error={missing(key)}
      helperText={missing(key) ? 'wymagane do zapisu w TMS' : ' '}
      slotProps={{ inputLabel: type === 'date' || type === 'time' ? { shrink: true } : undefined, htmlInput: { 'aria-required': true, 'data-field-key': key } }}
    />
  );

  return (
    <SectionCard title="Dane dla importera TMS">
      <Box data-testid="pre-order-importer-fields">
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Wszystkie pola są wymagane przez import w TMS, ale prezlecenie można zapisać niekompletne. Nieczytelne zostawiamy
          puste — nie zgadujemy. Po utworzeniu zlecenia te dane poprawia się w zleceniu.
        </Typography>
        {refusal ? <Alert severity="info" sx={{ mb: 1 }}>{refusal}</Alert> : null}
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        {!details ? (
          error ? null : <CircularProgress size={20} />
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Zleceniodawca</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: FIELD_GRID, gap: 1 }}>
              {ORDERING_PARTY_FIELDS.map(([k, label]) => field(`orderingParty.${k}`, label, party[k], (v) => setParty(k, v)))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: FIELD_GRID, gap: 1 }}>
              {field('volume', 'Objętość przesyłki (m³)', details.objetosc, (v) => setDetails((d) => ({ ...(d ?? {}), objetosc: v === '' ? null : Number(v) })), 'number')}
            </Box>
            <Divider />
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
              <Typography variant="subtitle2">Punkty ({stations.length})</Typography>
              {!readOnly && stations.length === 0 ? (
                <Button size="small" onClick={() => setDetails((d) => ({ ...(d ?? {}), stacje: stationsFromHeader(record) }))} data-testid="importer-stations-from-header">
                  Punkty z danych prezlecenia
                </Button>
              ) : null}
              {!readOnly ? (
                <Button size="small" startIcon={<AddIcon />} onClick={() => setDetails((d) => ({ ...(d ?? {}), stacje: [...(d?.stacje ?? []), {}] }))}>
                  Dodaj punkt
                </Button>
              ) : null}
            </Stack>
            {stations.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Bez punktów plik dostanie dwa z nagłówka prezlecenia (załadunek i rozładunek) — z brakami adresu i okien.
              </Typography>
            ) : null}
            {stations.map((s, i) => (
              <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }} data-testid="importer-station">
                <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
                    Punkt {i + 1}
                    {i === 0 ? ' (załadunek)' : i === stations.length - 1 ? ' (rozładunek)' : ''}
                  </Typography>
                  {!readOnly ? (
                    <Tooltip title="Usuń punkt">
                      <IconButton size="small" aria-label="Usuń punkt" onClick={() => setDetails((d) => ({ ...(d ?? {}), stacje: (d?.stacje ?? []).filter((_, j) => j !== i) }))}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: FIELD_GRID, gap: 1 }}>
                  {STATION_FIELDS.map(([k, label, type]) => field(`station.${i}.${k}`, label, s[k], (v) => setStation(i, k, v), type))}
                </Box>
              </Box>
            ))}
            {forkliftRequired ? (
              <>
                <Divider />
                <Typography variant="subtitle2">Czynność wózkowa (drugi plik)</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: FIELD_GRID, gap: 1 }}>
                  {field('forklift.price', 'Cena czynności', details.forklift?.price, (v) => setForklift('price', v), 'number')}
                  {field('forklift.count', 'Liczba czynności', details.forklift?.count, (v) => setForklift('count', v), 'number')}
                </Box>
              </>
            ) : null}
            {!readOnly ? (
              <Box>
                <Button variant="contained" size="small" startIcon={<SaveIcon />} disabled={busy} onClick={() => void save()} data-testid="importer-fields-save">
                  Zapisz dane dla importera
                </Button>
              </Box>
            ) : null}
          </Stack>
        )}
      </Box>
    </SectionCard>
  );
};
